import React, { useState, useEffect, useMemo, useRef } from 'react';
import { collection, doc, setDoc, updateDoc, deleteDoc, onSnapshot, writeBatch } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { STORE_LOCATIONS } from '../constants';
import { slugifyStoreName } from '../utils';
import type { StoreLocation } from '../types';
import {
  MapPin, Plus, AlertCircle, CheckCircle, Trash2, Pencil, Check, X, Search,
  ArrowUp, ArrowDown, ArrowUpDown, ChevronLeft, ChevronRight, DownloadCloud, Loader2,
} from 'lucide-react';
import FluidTabs from './FluidTabs';

const POINTS_PER_PAGE = 10;
type SortColumn = 'name' | 'address' | 'phone';
type SortDirection = 'asc' | 'desc';

const EMPTY_FORM = { name: '', address: '', phone: '', ticariUnvan: '' };

export default function SalesPointManagement() {
  const [points, setPoints] = useState<StoreLocation[]>([]);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingForm, setEditingForm] = useState(EMPTY_FORM);
  const [sortColumn, setSortColumn] = useState<SortColumn>('name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
  const [currentPage, setCurrentPage] = useState(1);
  const [activeTab, setActiveTab] = useState<'create' | 'list'>('list');
  const [searchQuery, setSearchQuery] = useState('');
  const [isSeeding, setIsSeeding] = useState(false);
  const hasAutoSeeded = useRef(false);

  const seedDefaults = async () => {
    if (!db) return;
    setIsSeeding(true);
    setError(null);
    try {
      let batch = writeBatch(db);
      let count = 0;
      for (const store of STORE_LOCATIONS) {
        const id = slugifyStoreName(store.name);
        batch.set(doc(db, 'sales_points', id), {
          name: store.name,
          address: store.address,
          phone: store.phone,
          ticariUnvan: store.ticariUnvan,
        }, { merge: true });
        count++;
        if (count >= 25) { await batch.commit(); batch = writeBatch(db); count = 0; }
      }
      if (count > 0) await batch.commit();
    } catch (err: any) {
      console.error('Varsayılan liste aktarılırken hata:', err);
      setError('Mevcut satış noktası listesi otomatik olarak yüklenirken bir hata oluştu: ' + err.message);
    } finally {
      setIsSeeding(false);
    }
  };

  useEffect(() => {
    if (!db) return;
    const unsubscribe = onSnapshot(collection(db, 'sales_points'), (snapshot) => {
      if (snapshot.empty) {
        setPoints([]);
        setLoaded(true);
        // First time this admin screen sees a truly empty collection: bring in the
        // existing built-in sales point list automatically, so the screen isn't
        // empty on first open. Guarded by a ref so it only ever fires once per mount.
        if (!hasAutoSeeded.current) {
          hasAutoSeeded.current = true;
          seedDefaults();
        }
        return;
      }
      const list: StoreLocation[] = [];
      snapshot.forEach(docSnap => {
        const data = docSnap.data();
        list.push({
          id: docSnap.id,
          name: data.name || '',
          address: data.address || '',
          phone: data.phone || '',
          ticariUnvan: data.ticariUnvan || '',
        });
      });
      setPoints(list);
      setLoaded(true);
    }, (err) => {
      console.error('Satış noktaları yüklenirken hata:', err);
      setLoaded(true);
    });
    return () => unsubscribe();
  }, []);

  const handleSortClick = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
    setCurrentPage(1);
  };

  const handleSearchChange = (value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  };

  const filteredPoints = useMemo(() => {
    const query = searchQuery.trim().toLocaleLowerCase('tr');
    if (!query) return points;
    return points.filter(p =>
      p.name.toLocaleLowerCase('tr').includes(query) ||
      p.address.toLocaleLowerCase('tr').includes(query)
    );
  }, [points, searchQuery]);

  const sortedPoints = useMemo(() => {
    const sorted = [...filteredPoints].sort((a, b) =>
      (a[sortColumn] || '').localeCompare(b[sortColumn] || '', 'tr', { sensitivity: 'base' })
    );
    if (sortDirection === 'desc') sorted.reverse();
    return sorted;
  }, [filteredPoints, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sortedPoints.length / POINTS_PER_PAGE));

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [totalPages, currentPage]);

  const paginatedPoints = sortedPoints.slice(
    (currentPage - 1) * POINTS_PER_PAGE,
    currentPage * POINTS_PER_PAGE
  );

  const nameExists = (name: string, excludeId?: string) =>
    points.some(p => p.id !== excludeId && p.name.trim().toLocaleLowerCase('tr') === name.trim().toLocaleLowerCase('tr'));

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!db) return;
    const name = form.name.trim();
    if (!name) {
      setError('Lütfen satış noktası adını girin.');
      return;
    }
    if (nameExists(name)) {
      setError(`'${name}' isimli bir satış noktası zaten mevcut.`);
      return;
    }

    setLoading(true);
    setError(null);
    setSuccess(null);
    try {
      const id = slugifyStoreName(name);
      await setDoc(doc(db, 'sales_points', id), {
        name,
        address: form.address.trim(),
        phone: form.phone.trim(),
        ticariUnvan: form.ticariUnvan.trim(),
      });
      setSuccess(`'${name}' satış noktası olarak eklendi.`);
      setForm(EMPTY_FORM);
    } catch (err: any) {
      console.error('Satış noktası eklenirken hata:', err);
      setError('Satış noktası eklenirken bir hata oluştu: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleStartEdit = (p: StoreLocation) => {
    setEditingId(p.id || null);
    setEditingForm({ name: p.name, address: p.address, phone: p.phone, ticariUnvan: p.ticariUnvan });
  };

  const handleSaveEdit = async (id: string) => {
    if (!db) return;
    const name = editingForm.name.trim();
    if (!name) return;
    if (nameExists(name, id)) {
      setError(`'${name}' isimli bir satış noktası zaten mevcut.`);
      return;
    }
    try {
      await updateDoc(doc(db, 'sales_points', id), {
        name,
        address: editingForm.address.trim(),
        phone: editingForm.phone.trim(),
        ticariUnvan: editingForm.ticariUnvan.trim(),
      });
      setSuccess(`'${name}' satış noktası güncellendi.`);
      setEditingId(null);
    } catch (err: any) {
      console.error('Satış noktası güncellenirken hata:', err);
      setError('Satış noktası güncellenirken bir hata oluştu: ' + err.message);
    }
  };

  const handleDelete = async (p: StoreLocation) => {
    if (!db || !p.id) return;
    if (!window.confirm(`'${p.name}' satış noktasını silmek istediğinize emin misiniz? Bu satış noktasına atanmış kullanıcılar/teklifler etkilenmez, yalnızca yeni seçim listesinden kalkar.`)) {
      return;
    }
    setError(null);
    setSuccess(null);
    try {
      await deleteDoc(doc(db, 'sales_points', p.id));
      setSuccess(`'${p.name}' satış noktası silindi.`);
    } catch (err: any) {
      console.error('Satış noktası silinirken hata:', err);
      setError('Satış noktası silinirken bir hata oluştu: ' + err.message);
    }
  };

  const handleSeedDefaults = async () => {
    if (!window.confirm(`Varsayılan ${STORE_LOCATIONS.length} satış noktası listesi buluta aktarılsın mı?`)) return;
    setSuccess(null);
    await seedDefaults();
    setSuccess('Varsayılan satış noktası listesi buluta aktarıldı.');
  };

  return (
    <div className="p-4 space-y-6">
      <div className="flex justify-center">
        <FluidTabs
          activeId={activeTab}
          onChange={(id) => setActiveTab(id as 'create' | 'list')}
          tabs={[
            { id: 'create', label: 'Satış Noktası Ekle', icon: <Plus className="w-3.5 h-3.5" /> },
            { id: 'list', label: 'Satış Noktaları', icon: <MapPin className="w-3.5 h-3.5" /> },
          ]}
        />
      </div>

      {error && (
        <div className="max-w-3xl mx-auto p-3 bg-red-50 border-l-2 border-red-500 rounded text-red-700 text-xs flex items-start space-x-1.5">
          <AlertCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}
      {success && (
        <div className="max-w-3xl mx-auto p-3 bg-emerald-50 border-l-2 border-emerald-500 rounded text-emerald-700 text-xs flex items-start space-x-1.5">
          <CheckCircle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
          <span>{success}</span>
        </div>
      )}

      {activeTab === 'create' && (
        <div className="w-full md:w-[32rem] mx-auto bg-white border border-stone-200 rounded-2xl p-6 shadow-xs h-fit">
          <div className="flex items-center space-x-2 border-b border-stone-100 pb-3 mb-5">
            <MapPin className="w-4 h-4 text-brand-600" />
            <h2 className="text-xs uppercase tracking-wider font-bold text-stone-900">Satış Noktası Ekle</h2>
          </div>

          <form onSubmit={handleCreate} className="space-y-5">
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">
                Satış Noktası Adı
              </label>
              <input
                type="text"
                required
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="İntema Adana Mağaza"
                className="w-full px-3 py-2.5 text-xs bg-stone-50/50 border border-stone-200 rounded-xl focus:border-brand-500 focus:outline-none transition-colors"
              />
            </div>
            <div>
              <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">
                Adres
              </label>
              <textarea
                rows={2}
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Açık adres..."
                className="w-full px-3 py-2.5 text-xs bg-stone-50/50 border border-stone-200 rounded-xl focus:border-brand-500 focus:outline-none transition-colors resize-none"
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">
                  Telefon
                </label>
                <input
                  type="text"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  placeholder="(0322) 457 96 63"
                  className="w-full px-3 py-2.5 text-xs bg-stone-50/50 border border-stone-200 rounded-xl focus:border-brand-500 focus:outline-none transition-colors"
                />
              </div>
              <div>
                <label className="block text-[10px] font-bold text-stone-400 uppercase tracking-wider mb-2">
                  Ticari Unvan
                </label>
                <input
                  type="text"
                  value={form.ticariUnvan}
                  onChange={(e) => setForm({ ...form, ticariUnvan: e.target.value })}
                  placeholder="... A.Ş."
                  className="w-full px-3 py-2.5 text-xs bg-stone-50/50 border border-stone-200 rounded-xl focus:border-brand-500 focus:outline-none transition-colors"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#945f46] hover:bg-[#83523c] text-white text-xs font-bold py-2.5 px-4 rounded-xl shadow-xs transition-colors flex items-center justify-center space-x-1.5 tracking-wider uppercase disabled:opacity-75 disabled:cursor-not-allowed mt-4"
            >
              {loading ? (
                <>
                  <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  <span>Ekleniyor...</span>
                </>
              ) : (
                <>
                  <Plus className="w-3.5 h-3.5" />
                  <span>Satış Noktası Ekle</span>
                </>
              )}
            </button>
          </form>

          {loaded && points.length === 0 && (
            <div className="mt-4 pt-4 border-t border-stone-150 space-y-2">
              <button
                type="button"
                disabled={isSeeding}
                onClick={handleSeedDefaults}
                className="w-full py-2.5 px-3 text-xs bg-stone-100 hover:bg-stone-200 text-stone-700 font-bold rounded-xl flex items-center justify-center space-x-1.5 transition-colors border border-stone-200 disabled:opacity-50"
              >
                {isSeeding ? <Loader2 className="w-3.5 h-3.5 animate-spin text-stone-500" /> : <DownloadCloud className="w-3.5 h-3.5 text-stone-500" />}
                <span>Varsayılan Listeyi İçe Aktar ({STORE_LOCATIONS.length})</span>
              </button>
              <p className="text-[9px] text-stone-400 text-center leading-relaxed">
                Sistemde henüz kayıtlı satış noktası yok. Mevcut varsayılan listeyi buluta aktararak başlayabilir, sonrasında tek tek düzenleyebilirsiniz.
              </p>
            </div>
          )}
        </div>
      )}

      {activeTab === 'list' && (
        <div className="w-full bg-white border border-stone-200 rounded-2xl p-5 shadow-xs">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b border-stone-100 pb-3 mb-4">
            <div className="flex items-center space-x-2">
              <MapPin className="w-4 h-4 text-brand-600" />
              <h2 className="text-xs uppercase tracking-wider font-bold text-stone-900">
                Satış Noktaları ({filteredPoints.length}{searchQuery.trim() ? ` / ${points.length}` : ''})
              </h2>
            </div>
            <div className="relative w-full sm:w-64">
              <span className="absolute inset-y-0 left-0 pl-3 flex items-center text-stone-400">
                <Search className="w-3.5 h-3.5" />
              </span>
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => handleSearchChange(e.target.value)}
                placeholder="İsim veya adrese göre ara..."
                className="w-full pl-9 pr-3 py-1.5 text-xs bg-stone-50/50 border border-stone-200 rounded-xl focus:border-brand-500 focus:outline-none transition-colors placeholder:text-stone-400 placeholder:italic"
              />
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse text-xs">
              <thead>
                <tr className="border-b border-stone-200 text-stone-400 text-[10px] uppercase font-bold tracking-wider">
                  <th className="py-1.5 px-3">
                    <button type="button" onClick={() => handleSortClick('name')} className="flex items-center space-x-1 hover:text-stone-700 transition-colors whitespace-nowrap">
                      <span>Satış Noktası</span>
                      {sortColumn === 'name' ? (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                    </button>
                  </th>
                  <th className="py-1.5 px-3">
                    <button type="button" onClick={() => handleSortClick('address')} className="flex items-center space-x-1 hover:text-stone-700 transition-colors">
                      <span>Adres</span>
                      {sortColumn === 'address' ? (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                    </button>
                  </th>
                  <th className="py-1.5 px-3 w-40">
                    <button type="button" onClick={() => handleSortClick('phone')} className="flex items-center space-x-1 hover:text-stone-700 transition-colors">
                      <span>Telefon</span>
                      {sortColumn === 'phone' ? (sortDirection === 'asc' ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />) : <ArrowUpDown className="w-3 h-3 opacity-40" />}
                    </button>
                  </th>
                  <th className="py-1.5 px-3 w-56">Ticari Unvan</th>
                  <th className="py-1.5 px-3 w-20 text-right">İşlemler</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-stone-100 font-medium text-stone-700">
                {paginatedPoints.map((p) => {
                  const isEditing = editingId === p.id;
                  return (
                    <tr key={p.id} className="hover:bg-stone-50/30 transition-colors align-top">
                      <td className="py-1.5 px-3 font-bold text-stone-900 max-w-64">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editingForm.name}
                            onChange={(e) => setEditingForm({ ...editingForm, name: e.target.value })}
                            autoFocus
                            className="w-full px-2 py-1 text-xs border border-brand-400 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 bg-white"
                          />
                        ) : (
                          <span className="whitespace-normal">{p.name}</span>
                        )}
                      </td>
                      <td className="py-1.5 px-3 max-w-80">
                        {isEditing ? (
                          <textarea
                            rows={2}
                            value={editingForm.address}
                            onChange={(e) => setEditingForm({ ...editingForm, address: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-brand-400 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 bg-white resize-none"
                          />
                        ) : (
                          <span className="whitespace-normal">{p.address}</span>
                        )}
                      </td>
                      <td className="py-1.5 px-3 whitespace-nowrap">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editingForm.phone}
                            onChange={(e) => setEditingForm({ ...editingForm, phone: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-brand-400 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 bg-white"
                          />
                        ) : (
                          p.phone || '-'
                        )}
                      </td>
                      <td className="py-1.5 px-3 max-w-56">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editingForm.ticariUnvan}
                            onChange={(e) => setEditingForm({ ...editingForm, ticariUnvan: e.target.value })}
                            className="w-full px-2 py-1 text-xs border border-brand-400 rounded focus:outline-none focus:ring-1 focus:ring-brand-500 bg-white"
                          />
                        ) : (
                          <span className="whitespace-normal text-stone-500">{p.ticariUnvan || '-'}</span>
                        )}
                      </td>
                      <td className="py-1.5 px-3 text-right">
                        {isEditing ? (
                          <div className="flex items-center justify-end space-x-1">
                            <button
                              type="button"
                              onClick={() => p.id && handleSaveEdit(p.id)}
                              className="p-1 bg-emerald-600 hover:bg-emerald-700 text-white rounded shadow-2xs"
                              title="Kaydet"
                            >
                              <Check className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => setEditingId(null)}
                              className="p-1 bg-stone-200 hover:bg-stone-300 text-stone-700 rounded"
                              title="İptal"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        ) : (
                          <div className="flex items-center justify-end space-x-1">
                            <button
                              type="button"
                              onClick={() => handleStartEdit(p)}
                              title="Düzenle"
                              className="p-1 text-stone-400 hover:text-brand-700 hover:bg-stone-100 rounded transition-colors"
                            >
                              <Pencil className="w-3.5 h-3.5" />
                            </button>
                            <button
                              type="button"
                              onClick={() => handleDelete(p)}
                              title="Sil"
                              className="p-1 text-stone-400 hover:text-red-600 hover:bg-stone-100 rounded transition-colors"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
                {loaded && points.length === 0 && isSeeding && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-stone-400">
                      <span className="inline-flex items-center gap-1.5">
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        Mevcut satış noktası listesi yükleniyor...
                      </span>
                    </td>
                  </tr>
                )}
                {loaded && points.length === 0 && !isSeeding && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-stone-400">
                      Henüz kayıtlı satış noktası yok. "Satış Noktası Ekle" sekmesinden varsayılan listeyi içe aktarabilir veya yeni bir tane ekleyebilirsiniz.
                    </td>
                  </tr>
                )}
                {!loaded && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-stone-400">
                      Satış noktaları yükleniyor...
                    </td>
                  </tr>
                )}
                {loaded && points.length > 0 && filteredPoints.length === 0 && (
                  <tr>
                    <td colSpan={5} className="py-6 text-center text-stone-400">
                      "{searchQuery}" ile eşleşen satış noktası bulunamadı.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {filteredPoints.length > 0 && (
            <div className="flex items-center justify-between pt-4 mt-2 border-t border-stone-100">
              <span className="text-[10px] font-bold text-stone-400 uppercase tracking-wider">
                Sayfa {currentPage} / {totalPages} · Toplam {filteredPoints.length} satış noktası
              </span>
              <div className="flex items-center space-x-2">
                <button
                  type="button"
                  disabled={currentPage <= 1}
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  className="flex items-center space-x-1 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-600 bg-stone-50 border border-stone-200 rounded-lg hover:bg-stone-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-stone-50"
                >
                  <ChevronLeft className="w-3.5 h-3.5" />
                  <span>Geri</span>
                </button>
                <button
                  type="button"
                  disabled={currentPage >= totalPages}
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  className="flex items-center space-x-1 px-2.5 py-1.5 text-[10px] font-bold uppercase tracking-wider text-stone-600 bg-stone-50 border border-stone-200 rounded-lg hover:bg-stone-100 transition-colors disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:bg-stone-50"
                >
                  <span>İleri</span>
                  <ChevronRight className="w-3.5 h-3.5" />
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
